import { type RuleCandidate } from '@components/Customization/PoiTypes/RuleEditor';
import { FieldTypeIcon } from '@components/Customization/fieldTypes';
import { followUpAnswers, readVisibility, triggerOf } from '@lib/customization/poiDesign';
import { useLocalization } from '@lib/localization';
import { Divider, ListItemIcon, ListSubheader, Menu, MenuItem } from '@mui/material';
import { formInputTypes, type FormInputType, type LocalizedText, type PoiField, type PoiFieldGroup } from '@store/api/poiTypeEndpoints';
import { ArrowDownIcon, ArrowUpIcon, CopyIcon, LayerGroupIcon, PlusIcon, TrashIcon } from 'react-line-awesome';
import { answerLabel, branchWords } from './describe';
import { type CanvasActions } from './DesignCanvas';

/** Where a menu opens: at a button, or where a right-click was. */
export type MenuAnchor = { element: HTMLElement } | { point: { top: number; left: number } };

export type MenuTarget =
  | { kind: 'field'; field: PoiField; nested: boolean; move?: { up?: () => void; down?: () => void } }
  | { kind: 'followUps'; field: PoiField }
  | { kind: 'group'; group: PoiFieldGroup; index: number }
  | { kind: 'canvas' };

const anchorProps = (at: MenuAnchor) => ('point' in at
  ? { anchorReference: 'anchorPosition' as const, anchorPosition: at.point }
  : { anchorEl: at.element });

interface Item {
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  /** Asks for a field type first, at the same place, then does this with it. */
  withType?: (type: FormInputType) => void;
  danger?: boolean;
  divider?: boolean;
  header?: boolean;
}

interface ActionMenuProps {
  at: MenuAnchor;
  target: MenuTarget;
  actions: CanvasActions;
  candidates: ReadonlyMap<string, RuleCandidate>;
  resolve: (text?: LocalizedText) => string;
  onPickType: (pick: (type: FormInputType) => void) => void;
  onClose: () => void;
}

/** Everything that can be done to what was clicked, in one place. */
export const ActionMenu: React.FC<ActionMenuProps> = ({ at, target, actions, candidates, resolve, onPickType, onClose }) => {
  const { t } = useLocalization();

  const followUps = (field: PoiField): Item[] => [
    { label: t('Customization_Designer_AskNext'), header: true },
    ...followUpAnswers(field).map((answer) => ({
      label: answerLabel(answer, resolve),
      withType: (type: FormInputType) => actions.addFollowUp(field.id!, answer.condition, type),
    })),
  ];

  const itemsFor = (): Item[] => {
    switch (target.kind) {
      case 'followUps':
        return followUps(target.field);
      case 'field': {
        const { field, nested, move } = target;
        const id = field.id ?? '';
        const visibility = readVisibility(field);
        const trigger = triggerOf(field);

        return [
          ...followUps(field),
          { label: '', divider: true },
          // A follow-up's sibling belongs to the same answer; a top-level field's goes after its block.
          nested && trigger && visibility.mode === 'when'
            ? {
              label: t('Customization_Designer_AddSibling', [branchWords(visibility, candidates, resolve)]),
              icon: <PlusIcon />,
              withType: (type: FormInputType) => actions.addFollowUp(trigger, visibility.conditions[0], type, visibility),
            }
            : { label: t('Customization_Designer_InsertBelow'), icon: <PlusIcon />, withType: (type: FormInputType) => actions.insertAfter(id, type) },
          { label: t('Customization_Duplicate'), icon: <CopyIcon />, onClick: () => actions.duplicate(id) },
          ...(move
            ? [
              { label: t('Customization_Designer_MoveUp'), icon: <ArrowUpIcon />, onClick: move.up },
              { label: t('Customization_Designer_MoveDown'), icon: <ArrowDownIcon />, onClick: move.down },
            ]
            : []),
          { label: '', divider: true },
          { label: t('Common_Delete'), icon: <TrashIcon />, danger: true, onClick: () => actions.remove(id) },
        ];
      }
      case 'group':
        return [
          {
            label: t('Customization_Designer_AddFieldTo', [resolve(target.group.name) || t('Customization_NewGroup')]),
            icon: <PlusIcon />,
            withType: (type: FormInputType) => actions.addField(target.index, type),
          },
          { label: '', divider: true },
          { label: t('Customization_DeleteGroup'), icon: <TrashIcon />, danger: true, onClick: () => actions.removeGroup(target.group.id ?? '') },
        ];
      case 'canvas':
        return [
          { label: t('Customization_AddField'), icon: <PlusIcon />, withType: (type: FormInputType) => actions.addField('type', type) },
          { label: t('Customization_AddGroup'), icon: <LayerGroupIcon />, onClick: actions.addGroup },
        ];
    }
  };

  return (
    <Menu
      open
      {...anchorProps(at)}
      onClose={onClose}
      onClick={(event) => event.stopPropagation()}
      onContextMenu={(event) => {
        event.preventDefault();
        onClose();
      }}
      disableRestoreFocus
      slotProps={{ paper: { sx: { minWidth: 220 } } }}
    >
      {itemsFor().map((item, index) => {
        if (item.divider) return <Divider key={index} />;
        if (item.header) return <ListSubheader key={index} sx={{ lineHeight: '32px' }}>{item.label}</ListSubheader>;

        return (
          <MenuItem
            key={index}
            disabled={!item.onClick && !item.withType}
            sx={item.danger ? { color: 'error.light' } : undefined}
            onClick={() => {
              onClose();
              if (item.withType) onPickType(item.withType);
              else item.onClick?.();
            }}
          >
            {item.icon && <ListItemIcon sx={item.danger ? { color: 'inherit' } : undefined}>{item.icon}</ListItemIcon>}
            {item.label}
          </MenuItem>
        );
      })}
    </Menu>
  );
};

interface TypeMenuProps {
  at: MenuAnchor | null;
  onPick: (type: FormInputType) => void;
  onClose: () => void;
}

/** The seven field types, for a field about to be added. */
export const TypeMenu: React.FC<TypeMenuProps> = ({ at, onPick, onClose }) => {
  const { t } = useLocalization();

  return (
    <Menu
      {...(at ? anchorProps(at) : { anchorEl: null })}
      open={Boolean(at)}
      onClose={onClose}
      // The new field's question box takes focus: neither handing it back to the button nor
      // the closing menu's focus trap may take it away again.
      disableRestoreFocus
      disableEnforceFocus
      onClick={(event) => event.stopPropagation()}
    >
      <ListSubheader sx={{ lineHeight: '32px' }}>{t('Customization_FieldType')}</ListSubheader>
      {formInputTypes.map((type) => (
        <MenuItem
          key={type}
          onClick={() => {
            onPick(type);
            onClose();
          }}
        >
          <ListItemIcon><FieldTypeIcon type={type} size={22} /></ListItemIcon>
          {t(`Customization_FieldType_${type}`)}
        </MenuItem>
      ))}
    </Menu>
  );
};
